import Cookies from 'universal-cookie';
import config from '../lib/config';
import { logger } from '../lib/logger';

export const LOGIN_UI = Object.freeze({
  AOSW: 'aosw',
  COMMON_LOGIN_IDENTITY_SPA: 'common-login-identity-spa',
});

export const COMMON_LOGIN_IDENTITY_SPA_ROLLOUT_MODE = Object.freeze({
  OFF: 'off',
  GRADUAL: 'gradual',
  GLOBAL: 'global',
});

// Explicit override for QA, puppeteer, and support. This controls only which
// login UI renders; it is unrelated to the Unified Patient Login rollout.
export const COMMON_LOGIN_IDENTITY_SPA_OVERRIDE_COOKIE = 'lwa_common_login_identity_spa_override';

// Persists iam-core's Common SPA assignment across the Okta redirect.
export const COMMON_LOGIN_IDENTITY_SPA_ROLLOUT_COOKIE = 'rollout_common_login_identity_spa';

const DAY_MILLISECONDS = 24 * 60 * 60 * 1000;
const COMMON_LOGIN_IDENTITY_SPA_ROLLOUT_TTL_DAYS = 180;

export const COMMON_LOGIN_IDENTITY_SPA_PAGE_BY_HASH = Object.freeze({
  '#create': '/signup',
  '#signin/athena-forgot-password': '/forgot-password',
});

const cookies = new Cookies();
const isLoginUi = (value) => Object.values(LOGIN_UI).includes(value);
const isPageEnabled = (enabledPages, page) =>
  Array.isArray(enabledPages) && (enabledPages.includes('*') || enabledPages.includes(page));

export function getCommonLoginIdentitySpaPage({ hash = '', originalAuthorizeUrl } = {}) {
  const currentPage = COMMON_LOGIN_IDENTITY_SPA_PAGE_BY_HASH[hash];
  if (currentPage) return currentPage;

  try {
    return COMMON_LOGIN_IDENTITY_SPA_PAGE_BY_HASH[new URL(originalAuthorizeUrl).hash];
  } catch (error) {
    return undefined;
  }
}

/**
 * Consumes iam-core's resolved Common SPA rollout decision.
 *
 * In gradual mode, iam-core owns the percentage. LWA persists only a positive
 * assignment so enrolled users remain on Common SPA as the percentage changes.
 * The rollout mode controls the audience; the page allowlist remains independent.
 */
export function getCommonLoginIdentitySpaRolloutTarget({
  commonLoginIdentitySpaRollout,
  rolloutMode = config.commonLoginIdentitySpaRolloutMode,
  commonLoginIdentitySpaEnabledPages = config.commonLoginIdentitySpaEnabledPages,
  page,
} = {}) {
  const override = cookies.get(COMMON_LOGIN_IDENTITY_SPA_OVERRIDE_COOKIE);
  if (isLoginUi(override)) {
    logger.info(`Login UI: ${override} (Common SPA override cookie)`);
    return override;
  }

  if (rolloutMode === COMMON_LOGIN_IDENTITY_SPA_ROLLOUT_MODE.OFF) {
    return LOGIN_UI.AOSW;
  }

  const pageEnabled = isPageEnabled(commonLoginIdentitySpaEnabledPages, page);
  if (rolloutMode === COMMON_LOGIN_IDENTITY_SPA_ROLLOUT_MODE.GLOBAL) {
    return pageEnabled ? LOGIN_UI.COMMON_LOGIN_IDENTITY_SPA : LOGIN_UI.AOSW;
  }

  if (rolloutMode !== COMMON_LOGIN_IDENTITY_SPA_ROLLOUT_MODE.GRADUAL) {
    logger.info(`Login UI: ${LOGIN_UI.AOSW} (invalid Common SPA rollout mode)`);
    return LOGIN_UI.AOSW;
  }

  const hasStickyCommonSpaDecision =
    cookies.get(COMMON_LOGIN_IDENTITY_SPA_ROLLOUT_COOKIE) === LOGIN_UI.COMMON_LOGIN_IDENTITY_SPA;

  if (!hasStickyCommonSpaDecision && commonLoginIdentitySpaRollout === true) {
    cookies.set(COMMON_LOGIN_IDENTITY_SPA_ROLLOUT_COOKIE, LOGIN_UI.COMMON_LOGIN_IDENTITY_SPA, {
      path: '/',
      httpOnly: false,
      secure: true,
      expires: new Date(Date.now() + (DAY_MILLISECONDS * COMMON_LOGIN_IDENTITY_SPA_ROLLOUT_TTL_DAYS)),
    });
  }

  if (!pageEnabled) {
    return LOGIN_UI.AOSW;
  }

  if (hasStickyCommonSpaDecision || commonLoginIdentitySpaRollout === true) {
    logger.info(`Login UI: ${LOGIN_UI.COMMON_LOGIN_IDENTITY_SPA} (Common SPA rollout, page=${page})`);
    return LOGIN_UI.COMMON_LOGIN_IDENTITY_SPA;
  }

  return LOGIN_UI.AOSW;
}
